import { portalApi } from "./client";
import type { TpsStage } from "../dashboard/journey";

// TPS Servicing Portal PR-C — portal-side client + DTO types for the
// third-party servicer's assigned-loans roster. Mirrors the C# records the
// backend exposes under /api/portal/tps/loans. Kept inside the portal
// bundle so nothing here imports staff types.

export type TpsLoanAcceptanceStatus = "Pending" | "Accepted" | "Declined";

export interface TpsLoanSummary {
  loanId: string;
  loanNumber: string;
  borrowerName: string;
  accountState: string;
  currentBalance: number;
  acceptanceStatus: TpsLoanAcceptanceStatus;
  acceptedAt: string | null;
  declineReason: string | null;
  assignedAt: string;
  /** F-32/F-33 — the borrower's governing US state (normalized 2-letter, null
   *  when unresolved). Lets the Home dashboard warn about a missing
   *  debt-collector disclosure before the accept gate 400s, and compute true
   *  DCL coverage. */
  borrowerState: string | null;
}

export interface TpsLoanParty {
  role: string;
  name: string;
  email: string | null;
  phone: string | null;
}

export interface TpsScheduleRow {
  periodNumber: number;
  dueDate: string;
  principal: number;
  interest: number;
  total: number;
  balanceAfter: number;
}

export interface TpsLoanDetail {
  summary: TpsLoanSummary;
  originalLoanAmount: number;
  // Stored as a fraction (0.075 = 7.5%); render × 100.
  interestRate: number;
  termMonths: number;
  maturityDate: string | null;
  firstPaymentDate: string | null;
  parties: TpsLoanParty[];
  schedule: TpsScheduleRow[];
  // PR-D — the borrower's governing US state (normalized 2-letter, null when
  // unresolved). Lets a blocked accept deep-link to that state's
  // debt-collector-language editor.
  borrowerState: string | null;
}

// Journey-Stage Adaptive Dashboards — the TPS Home readiness read. The ONE
// net-new endpoint the Home dashboard needs: certification + Stripe-onboarding
// signals plus the server-computed lifecycle stage (shared
// PortalJourneyStages.Tps classifier). The rest of the portfolio-at-a-glance is
// aggregated client-side from the existing tps* list endpoints.
export interface TpsReadiness {
  isCertified: boolean;
  stripeOnboarded: boolean;
  hasAcceptedLoans: boolean;
  /** Server-computed stage; the FE re-derives from the booleans for a
   *  guaranteed-valid union member. */
  stage: TpsStage;
}

export const tpsLoansApi = {
  list: () => portalApi.get<TpsLoanSummary[]>("/tps/loans"),
  get: (loanId: string) => portalApi.get<TpsLoanDetail>(`/tps/loans/${loanId}`),
  accept: (loanId: string) =>
    portalApi.post<TpsLoanDetail>(`/tps/loans/${loanId}/accept`),
  decline: (loanId: string, reason: string) =>
    portalApi.post<TpsLoanDetail>(`/tps/loans/${loanId}/decline`, { reason }),
  readiness: () => portalApi.get<TpsReadiness>("/tps/readiness"),
};
